import { Button } from '@fsd/ui';
import { Link } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import { ProductCard } from '@/components';
import { useProducts } from '@/hooks/useProducts';
import { useTranslation } from '@/i18n/hooks';
import { useInView } from '@/utils';

export function UsedDrones() {
  const { t, locale } = useTranslation('home');
  const { products: drones, loading } = useProducts('used-drones');

  const [sectionRef, isSectionInView] = useInView({ threshold: 0.1 });
  const [headerRef, isHeaderInView] = useInView({ threshold: 0.2 });

  const displayDrones = drones.slice(0, 3);

  return (
    <section
      ref={sectionRef as React.RefObject<HTMLElement>}
      id="used-drones"
      className="relative overflow-hidden bg-slate-50 py-24 sm:py-32 dark:bg-slate-900/50"
    >
      {/* Premium Background Decorations */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden text-slate-200/50 dark:text-slate-800/20">
        <svg
          className="absolute h-full w-full"
          width="100%"
          height="100%"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <defs>
            <pattern id="grid-used" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid-used)" />
        </svg>
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div ref={headerRef as React.RefObject<HTMLDivElement>} className="mb-16 md:mb-24">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary/10 text-secondary text-xs font-bold uppercase tracking-widest mb-6">
                <span className="relative flex h-2 w-2">
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-secondary"></span>
                </span>
                {t('usedDrones.badge')}
              </div>
              <h2
                className={`text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl dark:text-slate-100 slide-in-left-ready ${isHeaderInView ? 'in-view' : ''}`}
              >
                {t('usedDrones.title')}
              </h2>
              <p
                className={`mt-6 text-xl leading-relaxed text-slate-600 dark:text-slate-400 fade-in-ready ${isHeaderInView ? 'in-view' : ''}`}
                style={{ transitionDelay: '200ms' }}
              >
                {t('usedDrones.subtitle')}
              </p>
            </div>

            <div
              className={`fade-in-ready ${isHeaderInView ? 'in-view' : ''}`}
              style={{ transitionDelay: '400ms' }}
            >
              <Link to="/catalog" search={{ category: 'used', page: 1 }}>
                <Button
                  size="lg"
                  variant="secondary"
                  className="group rounded-full px-8 py-6 text-base font-bold shadow-2xl shadow-secondary/20 transition-all hover:scale-105 active:scale-95"
                >
                  {t('usedDrones.viewCatalog')}
                  <ArrowRight className="ml-3 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        <div className="relative">
          {loading ? (
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-[520px] w-full animate-pulse rounded-[2.5rem] bg-white dark:bg-slate-800/50"
                />
              ))}
            </div>
          ) : drones.length === 0 ? (
            <div className="relative overflow-hidden rounded-[3rem] border border-slate-200 bg-white/50 p-12 text-center dark:border-slate-800 dark:bg-slate-900/50 sm:p-24">
              <div className="relative mx-auto flex h-24 w-24 items-center justify-center rounded-4xl bg-white text-slate-400 shadow-xl dark:bg-slate-800">
                <svg
                  className="h-12 w-12"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <title>No used products</title>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                  />
                </svg>
              </div>
              <h3 className="relative mt-8 max-w-md mx-auto text-lg font-semibold tracking-tight text-slate-900 dark:text-slate-100">
                {t('usedDrones.noProducts')}
              </h3>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
              {displayDrones.map((drone, index) => (
                <div
                  key={drone.id}
                  className={`fade-in-up-ready ${isSectionInView ? 'in-view' : ''}`}
                  style={{ transitionDelay: `${300 + index * 150}ms` }}
                >
                  <ProductCard drone={drone} locale={locale} isUsed={true} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
